import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbDateStruct, NgbInputDatepicker, NgbModule, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { UtilityService } from '../../services/utility.service';
import { EditableFieldBase } from '../editable-field-base';
import { ErrorsWarningsComponent } from '../errors-warnings/errors-warnings.component';


@Component({
  selector: 'date-time',
  templateUrl: './date-time.component.html',
  styleUrls: ['./date-time.component.scss', '../editable-fields.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, NgbModule, ErrorsWarningsComponent],
})
export class DateTime extends EditableFieldBase implements OnInit {
  private readonly now: Date = new Date();

  @Input() public min: string;                // the string representation of the minimum month/year the user may select
  @Input() public max: string;                // the string representation of the maximum month/year the user may select 
  @Input() public allowEmpty: boolean;        // whether or not the control can be cleared to a null date
  @Input() public placeholder: string = ''    // placeholder text to use
  @Input() public allowDate: boolean = true;  // toggles the display of the date picker/value.  defaults to true for backwards compatibility.
  @Input() public allowTime: boolean;         // toggles the display of the time picker/value
  @Input() public dayOverride: number;        // sets a specific day for the component which triggers a month/year picker instead of a full date picker. typically set to 1 or 31.
  @Input() public isBlankReadOnlyText: string // optional for displaying text on a read-only blank input.
 
  @Input() updateValue: string;               //  Used when the UI doesn't update when a value is changed in typescript

  public dateModel: NgbDateStruct;
  public timeModel: NgbTimeStruct;

  //  Min/max dates
  public minDateModel: NgbDateStruct;
  public maxDateModel: NgbDateStruct;
  private minDate: Date = new Date(this.now.getFullYear() - 10, this.now.getMonth(), this.now.getDate());
  private maxDate: Date = new Date(this.now.getFullYear() + 10, this.now.getMonth(), this.now.getDate());

  // Used to keep the text inputs formatted appropriately
  protected _displayDateFormat: Intl.DateTimeFormatOptions;
  protected _displayDateValue: string;
  protected _displayTimeFormat: Intl.DateTimeFormatOptions;
  protected _displayTimeValue: string;

  //  Used in Month/Year picker
  public yearsMonthDetails: any = {};
  public selectedMonth: number;
  public selectedYear: number;
  public monthNames: string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  @ViewChild('datePicker', { static: false }) datePicker: NgbInputDatepicker;
  @ViewChild('focusButton') focusButton: ElementRef<HTMLButtonElement>;

  constructor(
    public utilities: UtilityService,
  ) {
    super();
  }

  public ngOnInit() {
    super.ngOnInit();

    if (!this.allowDate && !this.allowTime)
      throw new Error('Invalid configuration: allowDate or allowTime are both set to false.');

    if (!this.allowDate && !!this.dayOverride)
      throw new Error('Invalid configuration: must use allowDate to use dayOverride.');

    //  Pick the appropriate formatting for the UI if the default is not correct
    if (!!this.dayOverride)
      this._displayDateFormat = { month: 'short', year: 'numeric' };
    else {
      if (this.allowDate)
        this._displayDateFormat = { month: 'short', day: 'numeric', year: 'numeric' };
      if (this.allowTime)
        this._displayTimeFormat = { hour: 'numeric', minute: '2-digit', hour12: true };
    }

    if (!!this.min)
      this.minDate = new Date(this.min);

    if (!!this.max)
      this.maxDate ==new Date(this.max);

    this.minDateModel = { day: this.minDate.getUTCDate(), month: this.minDate.getUTCMonth() + 1, year: this.minDate.getUTCFullYear() };
    this.maxDateModel = { day: this.maxDate.getUTCDate(), month: this.maxDate.getUTCMonth() + 1, year: this.maxDate.getUTCFullYear() };

    let val: Date = new Date(this.obj[this.prop]);
    this.dateModel = { day: val.getDate(), month: (val.getMonth() + 1), year: val.getFullYear() } as NgbDateStruct;
    this.timeModel = { hour: val.getHours(), minute: val.getMinutes(), second: 0 };

    //  If we're building a month/year only picker then we need to create the options for the dropdowns
    if (!!this.dayOverride)
      this.setUpMonthYearData();

    this.formatDate();
  }

  public ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        let change = changes[propName];
        switch (propName) {
          case 'min':
            if (change.currentValue) {
              this.minDate = new Date(change.currentValue);
              this.minDateModel = { day: this.minDate.getUTCDate(), month: this.minDate.getUTCMonth() + 1, year: this.minDate.getUTCFullYear() };
            }
            break;
          case 'max':
            if (change.currentValue) {
              this.maxDate = new Date(change.currentValue);
              this.maxDateModel = { day: this.maxDate.getUTCDate(), month: this.maxDate.getUTCMonth() + 1, year: this.maxDate.getUTCFullYear() };
            }
            break;
          case 'updateValue':            
            const val: Date = new Date(changes[propName].currentValue);
            this.dateModel = { day: val.getDate(), month: val.getMonth() + 1, year: val.getFullYear() } as NgbDateStruct;
            this.timeModel = { hour: val.getHours(), minute: val.getMinutes(), second: 0 };
            this.formatDate(); // always update the display value
            break;
        }        
      }
    }
  }

  //  This opens the date picker and auto-focuses on the Close button.  This makes tabbing
  //  work consistently between the normal calendar picker and the month/year picker.
  public openFocusedPicker() {
    this.datePicker.toggle();

    const currentSelection = new Date(this.dateModel.year, this.dateModel.month - 1, this.dateModel.day);

    //  If the date is not set or it's out of bounds this makes it so the picker opens showing the current year and month (if applicable).
    if ((currentSelection < this.minDate) || (currentSelection > this.maxDate))
    {
      const month = this.dayOverride ? null : this.now.getMonth() + 1;
      this.datePicker?.navigateTo({ year: this.now.getFullYear(), month: month, day: null });

      if (!!this.dayOverride)
        this.selectedYear = this.now.getFullYear();
    }

    setTimeout(() => {
      this.focusButton?.nativeElement.focus();
    }); 
  }

  public onChange() {
    //  If we didn't receive a time from the API and the user hasn't entered one, default the time to midnight.
    if (!this.timeModel?.hour)
      this.timeModel = { hour: 0, minute: 0, second: 0 };

    //  Need to subtract 1 from the model month as new Date() uses base 0 for months and the NgbDateStruct uses base 1.
    if (!!this.dateModel) {
      const currentDate = new Date(this.dateModel.year, this.dateModel.month - 1, this.dateModel.day);

      //  If the date is a null date or this is a malformed date reset the value and calendar picker.
      if ((this.dateModel.year == 1900) || isNaN(currentDate.getTime()))
        this.clear();
      else
        this.obj[this.prop] = new Date(this.dateModel.year, this.dateModel.month - 1, this.dateModel.day, this.timeModel.hour, this.timeModel.minute).toUTCString();
    }

    //  This control instance only has one event - that the value is changed.  At that point it's submitted and
    //  that is when we can run validate and change.
    this.validate();
    this.formatDate();
    super.onChange();
  }

  public clear() {
    this.obj[this.prop] = UtilityService.nullDateString;
    this.dateModel = { day: UtilityService.nullDate.getDate(), month: UtilityService.nullDate.getMonth() + 1, year: UtilityService.nullDate.getFullYear() } as NgbDateStruct;

    if (this.allowTime)
      this.timeModel = { hour: null, minute: null, second: null };

    if (this.allowDate) {
      this.selectedYear = this.now.getFullYear();

      if (!!this.dayOverride)
        this.selectedMonth = null;
    }

    //  This control instance only has one event - that the value is changed.  At that point it's submitted and
    //  that is when we can run validate and change.
    this.validate();
    this.formatDate();
    super.onChange();
  }

  //  If the obj[prop] has been changed without use of the UI this will check the value against the model and update
  //  the UI for the calenda date picker that pops up.
  public updatePicker(datePicker) {
    //  If the date is coming back a null date it's causing the picker to go ~100 years back.  Setting the value to undefined instead
    //  of null or a null date ensures when the NbgDateStruct is formed for the picker it uses the current month/year.
    let checkDate = ((this.obj[this.prop] == UtilityService.nullDate) || (this.obj[this.prop] == UtilityService.nullDateString))
      ? undefined
      : this.obj[this.prop];

    let toDate = new Date(checkDate);
    const ngbObj = { day: toDate.getDate(), month: toDate.getMonth() + 1, year: toDate.getFullYear() } as NgbDateStruct;

    this.dateModel = ngbObj;

    datePicker.navigateTo(this.dateModel);

    this.formatDate();
  }

  public setToday() {
    // If this is from the month/year picker we let onMonthYearSelect() handle the updates it needs.
    if (!!this.dayOverride) {
      this.selectedYear = this.now.getFullYear();
      this.selectedMonth = this.now.getMonth() + 1;

      this.onMonthYearSelect();
      return;
    }

    if (!this.timeModel?.hour)
      this.timeModel = { hour: 0, minute: 0, second: 0 }

    this.dateModel = { day: this.now.getDate(), month: this.now.getMonth() + 1, year: this.now.getFullYear() } as NgbDateStruct;
    this.onChange();
  }

  //#region Time picker specific
  public onChangeTime(selectedTime: NgbTimeStruct) {
    //  Timepicker has a bug/feature where if you enter the hours but not the minutes this is returning the model as null.
    //  If that happens we'll wait until the minutes are provided to apply the time change, otherwise the UI will reset to
    //  midnight every time the user tries to set the hour for the first time.
    if (selectedTime != null) {
      //  If a date has not been picked first, set to today
      if (!this.dateModel.year || (this.dateModel.year == 1900)) {
        const val: Date = new Date();
        this.dateModel = { day: val.getDate(), month: val.getMonth() + 1, year: val.getFullYear() } as NgbDateStruct;
      }

      this.timeModel = selectedTime;
      this.obj[this.prop] = new Date(this.dateModel.year, this.dateModel.month - 1, this.dateModel.day, this.timeModel.hour, this.timeModel.minute).toUTCString();

      this.validate();
      this.formatDate();
      super.onChange();
    }
    else {
      // If this is only a time picker we clear the input.
      if (!this.allowDate) {
        this.obj[this.prop] = "";

      }
      else {
        //  If we don't have a time in a picker that allows both date and time then we end up in a weird
        //  place where we can't validate the lack of time.  As a workaroud we're forcing the time back
        //  to midnight.
        this.timeModel = { hour: 0, minute: 0, second: 0 };
        this.obj[this.prop] = new Date(this.dateModel.year, this.dateModel.month - 1, this.dateModel.day, this.timeModel.hour, this.timeModel.minute).toUTCString();
      }

      this.validate();
      this.formatDate();
      super.onChange();
    }
  }
  //#endregion

  //#region Text input specific
  public onStringChange() {
    if (!this._displayDateValue)
      this.clear()
    else {
      this.dateModel = this.parseDate(this._displayDateValue) as NgbDateStruct;
      this.onChange();
    }
  }

  private parseDate(value: string | NgbDateStruct) {
    if (!value)
      return null;

    if ((typeof (value) == 'object') && !!value?.year && !!value?.month && !!value?.day)
      return value;

    if (typeof (value) == 'string') {

      const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

      const date = value.split(/[\/\-,.\s]+/);
      let month = date[0];
      let day = date[1];
      let year = date[2];

      const monthIndex = months.findIndex(m => m.startsWith(month?.toLowerCase()));

      if (monthIndex > -1)
        month = (monthIndex + 1).toString();

      //  If this is a month/year only input we have to select the dates differently
      if (!!this.dayOverride) {
        year = date[2] ?? date[1];
        day = this.dayOverride.toString();
      }

      //  If we're missing a year or the day is missing and this isn't a month/year
      //  picker there's not enough data to make a valid date.
      if (!year?.length || !day?.length)
        return null;

      //  Clean up handling of years in different formats
      if (year.length != 4) {
        if (year.length < 2)
          year = `${2000 + (+year)}`;

        else if (year.length == 2)
          year = `20${year}`;

        else if (year.length == 3)
          year = `2${year}`;

        else if (year.length > 4)
          year = year.slice(-4);
      }

      let retVal = {
        month: +month,
        day: +day,
        year: +year,
      };

      //  If this is a month/year only input we have to clean up the dates and set
      //  values for the picker.
      if (!!this.dayOverride) {
        this.selectedYear = retVal.year;
        this.selectedMonth = retVal.month
        this.onMonthYearSelect();

        retVal = this.dateModel;
      }

      if (retVal.year == 1900)
        return null;

      return retVal;
    }

    return null;
  }

  private formatDate() {
    if (this.allowDate) {
      if (!this.dateModel || (this.dateModel.year == 1900) || isNaN(this.dateModel.year))
        this._displayDateValue = '';
      else {
        const newDate = new Date(this.dateModel.year, this.dateModel.month - 1, this.dateModel.day);
        this._displayDateValue = newDate.toLocaleString('en-US', this._displayDateFormat);
      }

      //  If the picker is still open close it.
      this.datePicker?.close();
    }

    if (this.allowTime) {
      if (!this.timeModel || isNaN(this.timeModel.hour))
        this._displayTimeValue = '';
      else {
        const newDate = new Date(this.dateModel.year, this.dateModel.month - 1, this.dateModel.day, this.timeModel.hour, this.timeModel.minute, 0);
        this._displayTimeValue = newDate.toLocaleString('en-US', this._displayTimeFormat);
      }
    }
  }
  //#endregion

  //#region Month/Year picker specific
  //  Does the initial work of setting up the data needed for a Month/Year only picker
  private setUpMonthYearData() {
    const minYear = this.minDate.getFullYear();
    const maxYear = this.maxDate.getFullYear();

    const years = [];
    for (let y = minYear; y <= maxYear; y++) {
      years.push(y);
    }

    let availableYearsMonths = {};
    for (let year of years) {
      availableYearsMonths[year] = [];

      let minMonth = year == minYear ? this.minDate.getMonth() + 1 : 1;
      let maxMonth = year == maxYear ? this.maxDate.getMonth() + 1 : 12;

      for (let i = minMonth; i <= maxMonth; i++)
        availableYearsMonths[year].push(this.monthNames[i - 1]);
    }

    if (!!this.obj[this.prop] && (this.obj[this.prop] != UtilityService.nullDateString)) {
      const newDateModel = this.cleanDayOverride(this.dateModel);
      const newDate = new Date(newDateModel.year, newDateModel.month - 1, newDateModel.day);
      this.dateModel = { day: newDate.getDate(), month: newDate.getMonth() + 1, year: newDate.getFullYear() };
      this.obj[this.prop] = new Date(this.dateModel.year, this.dateModel.month - 1, this.dateModel.day, this.timeModel.hour, this.timeModel.minute).toUTCString();
      this.formatDate();

      this.selectedYear = this.dateModel.year;
      this.selectedMonth = this.dateModel.day == 0 ? this.dateModel.month -1 : this.dateModel.month;
    }
    if (!this.dateModel?.year || (this.dateModel.year == 1900))
      this.selectedYear = new Date().getFullYear();

    this.yearsMonthDetails = {};
    this.yearsMonthDetails.availableDates = availableYearsMonths;

    const lowerMonth = this.yearsMonthDetails.availableDates[minYear][0].value;
    const upperMonth = this.yearsMonthDetails.availableDates[maxYear][this.yearsMonthDetails.availableDates[maxYear].length - 1].value;

    this.yearsMonthDetails.lowerBound = { year: minYear, month: lowerMonth };
    this.yearsMonthDetails.upperBound = { year: maxYear, month: upperMonth };
  }

  //  Provides standard calculations if we've selected that we want the last day of the month for a month/year picker
  private cleanDayOverride(date: NgbDateStruct) {
    const day = this.dayOverride == 31 ? 0 : this.dayOverride;
    const month = day == 0 ? date.month + 1 : date.month;

    return { month: month, day: day, year: date.year } as NgbDateStruct;
  }

  public onMonthYearSelect() {
    if (!!this.selectedMonth && !!this.selectedYear) {
      //  We need to clean up the values so they return correctly if we're choosing the end of month for the required date.
      const newDateModel = this.cleanDayOverride({ month: this.selectedMonth, day: this.dayOverride, year: this.selectedYear });
      const newDate = new Date(newDateModel.year, newDateModel.month -1, newDateModel.day);

      this.dateModel = { day: newDate.getDate(), month: newDate.getMonth() +1, year: newDate.getFullYear() };
      this.onChange();
    }
  }
  //#endregion
}
